import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormPage } from '@/ui/FormPage';
import { FormSection } from '@/ui/FormSection';
import { Input } from '@/ui/base/input';
import { Textarea } from '@/ui/base/textarea';
import { Label } from '@/ui/base/label';
import { Switch } from '@/ui/base/switch';
import { Spinner } from '@/ui/Spinner';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Shield, Info } from 'lucide-react';
import { toast } from 'sonner';
import {
  usePermissionProfile,
  useCreatePermissionProfile,
  useUpdatePermissionProfile,
} from '@/lib/usePermissionProfiles';
import {
  SYSTEM_ROLE_PERMISSIONS,
  SYSTEM_ROLE_LABELS,
  type PermissionsMap,
} from '@/lib/permissionModules';
import type { AppRole } from '@/lib/useUsers';
import { PermissionMatrix } from '@/modulos/admin/PermissionMatrix';

interface PerfilFormProps {
  systemRole?: AppRole;
}

export default function PerfilForm({ systemRole }: PerfilFormProps) {
  const { id, role: routeRole } = useParams();
  const navigate = useNavigate();
  const activeSystemRole = systemRole || (routeRole as AppRole | undefined);
  const isSystemView = !!activeSystemRole;
  const isEditing = !!id && !isSystemView;

  const { data: existing, isLoading } = usePermissionProfile(isEditing ? id : undefined);
  const createMutation = useCreatePermissionProfile();
  const updateMutation = useUpdatePermissionProfile();

  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [ativo, setAtivo] = useState(true);
  const [permissoes, setPermissoes] = useState<PermissionsMap>({});

  useEffect(() => {
    if (isSystemView && activeSystemRole) {
      const info = SYSTEM_ROLE_LABELS[activeSystemRole];
      setNome(info?.nome || activeSystemRole);
      setDescricao(info?.descricao || '');
      setPermissoes(SYSTEM_ROLE_PERMISSIONS[activeSystemRole] || {});
      setAtivo(true);
    }
  }, [isSystemView, activeSystemRole]);

  useEffect(() => {
    if (existing && !isSystemView) {
      setNome(existing.nome);
      setDescricao(existing.descricao || '');
      setAtivo(existing.ativo);
      setPermissoes(existing.permissoes);
    }
  }, [existing, isSystemView]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSystemView) return;
    if (!nome.trim()) {
      toast.error('Informe o nome do perfil');
      return;
    }
    try {
      if (isEditing) {
        await updateMutation.mutateAsync({ id, nome, descricao, permissoes, ativo });
        toast.success('Perfil atualizado com sucesso');
      } else {
        await createMutation.mutateAsync({ nome, descricao, permissoes, ativo });
        toast.success('Perfil criado com sucesso');
      }
      navigate('/admin/perfis');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao salvar perfil');
    }
  };

  if (isEditing && isLoading) return <Spinner size="lg" className="min-h-[50vh]" />;

  const saving = createMutation.isPending || updateMutation.isPending;
  const title = isSystemView ? `Perfil: ${nome}` : isEditing ? 'Editar Perfil' : 'Novo Perfil';

  return (
    <FormPage
      backTo="/admin/perfis"
      onSubmit={isSystemView ? (e) => e.preventDefault() : handleSubmit}
      submitLabel={isSystemView ? undefined : isEditing ? 'Salvar Alterações' : 'Criar Perfil'}
      isSaving={saving}
    >
      {isSystemView && (
        <Alert className="border-primary/30 bg-primary/5">
          <Shield className="h-4 w-4 text-primary" />
          <AlertDescription className="text-sm">
            Este é um perfil do sistema e não pode ser editado. As permissões abaixo refletem as
            políticas de acesso configuradas para o papel <strong>{nome}</strong>.
          </AlertDescription>
        </Alert>
      )}

      <FormSection title={title}>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="nome">Nome {!isSystemView && '*'}</Label>
            <Input
              id="nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Gestor Financeiro"
              disabled={isSystemView}
            />
          </div>
          {!isSystemView && (
            <div className="space-y-2 flex items-center gap-3 pt-6">
              <Switch id="ativo" checked={ativo} onCheckedChange={setAtivo} />
              <Label htmlFor="ativo">Perfil ativo</Label>
            </div>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="descricao">Descrição</Label>
          <Textarea
            id="descricao"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            placeholder="Descreva o propósito deste perfil"
            rows={2}
            disabled={isSystemView}
          />
        </div>
      </FormSection>

      <FormSection title="Matriz de Permissões">
        {isSystemView && (
          <p className="text-sm text-muted-foreground flex items-center gap-1.5 mb-2">
            <Info className="h-3.5 w-3.5" />
            Visualização somente leitura das permissões deste perfil do sistema.
          </p>
        )}
        <PermissionMatrix
          permissoes={permissoes}
          onChange={isSystemView ? undefined : setPermissoes}
          readOnly={isSystemView}
        />
      </FormSection>
    </FormPage>
  );
}
